/**
 * Data hooks for the Geography admin tab (WPS-222): the reference tables
 * (postal codes, PSA mappings, counties, MSAs, FIPS bridges, state defaults),
 * cross-product availability, the `meta/` option lists, and zone ownership
 * (grid, member picker, owner-change preview + set).
 *
 * Every list is a `LIST_LIVE` key (see lib/query-persistence/query-defaults.ts)
 * and every query is gated on the `geographic_availability` flag, mirroring
 * `use-geo.ts`. One small factory builds the CRUD hooks for each simple
 * resource so the eight sections share one contract.
 */

import { useCallback } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '@/admin/lib/api'
import { FLAGS, useFeatureFlag } from '@/admin/hooks/use-feature-flag'
import type { PaginatedResponse } from '@/shared/types/api'

type Params = Record<string, string | number | boolean | undefined>

function clean(params?: Params): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {}
  for (const [k, v] of Object.entries(params ?? {})) {
    if (v !== undefined && v !== '' && v !== null) out[k] = v
  }
  return out
}

/** Unwrap the `{status, message, result}` envelope our detail endpoints use. */
function result<T>(data: unknown): T {
  const env = data as { result?: T } | T
  return (env as { result?: T })?.result !== undefined
    ? ((env as { result: T }).result as T)
    : (env as T)
}

// ── Shared row shapes ──────────────────────────────────────────────────

export interface ZoneRef {
  zone_uuid: string
  zone_name: string
  zone_code: string
}

export interface PostalCodeRow extends ZoneRef {
  uuid: string
  postal_code: string
  country_code: string
  msa_uuid: string | null
  msa_code: string | null
  msa_title: string | null
}

export interface PostalCodeInput {
  postal_code: string
  country_code?: string
  zone_uuid: string
  msa_uuid?: string | null
}

export interface PsaMappingRow extends ZoneRef {
  uuid: string
  psa_uuid: string
  psa_name: string
  psa_code: string
  business: string
}

export interface PsaMappingInput {
  zone_uuid: string
  psa_uuid: string
  business: string
}

export interface CountyRow {
  uuid: string
  name: string
  state: string
  country_code: string
  fips_county_code: string
  fips_state_code: string
  zone_uuid: string | null
  zone_name: string | null
  zone_code: string | null
  msa_count: number
}

export interface FipsCountyZoneRow extends ZoneRef {
  uuid: string
  county_name: string
  state: string
}

export interface StateDefaultRow extends ZoneRef {
  uuid: string
  state: string
}

export interface StateDefaultInput {
  state: string
  zone_uuid: string
}

export interface MsaRow {
  uuid: string
  code: string
  title: string
  statistical_area: string
  country_code: string
  county_count: number
}

export interface AvailabilityRow extends ZoneRef {
  uuid: string
  product_uuid: string
  product_name: string
  product_sku: string
  product_template_uuid: string | null
  product_template_name: string | null
  effective_start: string
  effective_end: string | null
  is_active: boolean
}

export interface AvailabilityInput {
  product_uuid?: string
  zone_uuid?: string
  effective_start?: string
  effective_end?: string | null
}

// ── Generic resource factory ──────────────────────────────────────────

interface GeoResource<TRow, TInput> {
  useList: (params?: Params) => ReturnType<typeof useQuery<PaginatedResponse<TRow>>>
  useCreate: () => ReturnType<typeof useMutation<TRow, unknown, TInput>>
  useUpdate: () => ReturnType<
    typeof useMutation<TRow, unknown, { uuid: string; data: Partial<TInput> }>
  >
  useDelete: () => ReturnType<typeof useMutation<unknown, unknown, string>>
}

function makeGeoResource<TRow, TInput>(
  key: string,
  path: string,
  alsoInvalidate: string[] = [],
): GeoResource<TRow, TInput> {
  const invalidate = (qc: ReturnType<typeof useQueryClient>) => {
    qc.invalidateQueries({ queryKey: [key] })
    for (const extra of alsoInvalidate) qc.invalidateQueries({ queryKey: [extra] })
  }
  return {
    useList: (params?: Params) => {
      const enabled = useFeatureFlag(FLAGS.GEOGRAPHIC_AVAILABILITY)
      return useQuery<PaginatedResponse<TRow>>({
        queryKey: [key, clean(params)],
        queryFn: () => api.get(path, { params: clean(params) }).then((r) => r.data),
        enabled,
      })
    },
    useCreate: () => {
      const qc = useQueryClient()
      return useMutation<TRow, unknown, TInput>({
        mutationFn: (data) => api.post(path, data).then((r) => result<TRow>(r.data)),
        onSuccess: () => invalidate(qc),
      })
    },
    useUpdate: () => {
      const qc = useQueryClient()
      return useMutation<TRow, unknown, { uuid: string; data: Partial<TInput> }>({
        mutationFn: ({ uuid, data }) =>
          api.patch(`${path}${uuid}/`, data).then((r) => result<TRow>(r.data)),
        onSuccess: () => invalidate(qc),
      })
    },
    useDelete: () => {
      const qc = useQueryClient()
      return useMutation<unknown, unknown, string>({
        mutationFn: (uuid) => api.delete(`${path}${uuid}/`),
        onSuccess: () => invalidate(qc),
      })
    },
  }
}

export const postalCodes = makeGeoResource<PostalCodeRow, PostalCodeInput>(
  'geo-postal-codes',
  '/geo/postal-codes/',
  ['geo-zones', 'geo-zones-all', 'geo-meta'],
)
export const psaMappings = makeGeoResource<PsaMappingRow, PsaMappingInput>(
  'geo-psa-mappings',
  '/geo/psa-mappings/',
  ['geo-zones', 'geo-zones-all', 'geo-meta'],
)
export const counties = makeGeoResource<CountyRow, never>('geo-counties', '/geo/counties/', [
  'geo-meta',
])
export const fipsCountyZones = makeGeoResource<FipsCountyZoneRow, never>(
  'geo-fips-county-zones',
  '/geo/fips-county-zones/',
  ['geo-meta'],
)
export const stateDefaults = makeGeoResource<StateDefaultRow, StateDefaultInput>(
  'geo-state-defaults',
  '/geo/state-defaults/',
  ['geo-meta'],
)
export const msas = makeGeoResource<MsaRow, never>('geo-msas', '/geo/msas/', ['geo-meta'])
export const availability = makeGeoResource<AvailabilityRow, AvailabilityInput>(
  'geo-availability',
  '/geo/availability/',
  ['product-availability'],
)

// ── Meta ──────────────────────────────────────────────────────────────

export interface GeoOption {
  value: string
  label: string
}

export interface AssignmentChannel {
  key: string
  channel_key: string
  role: string
  label: string
  source: 'attribute' | 'zone_assignment'
  rule_key: string
  attribute_code: string
  attribute_display_name: string
  editable: boolean
}

export interface GeoMeta {
  zone_types: GeoOption[]
  businesses: GeoOption[]
  country_codes: GeoOption[]
  states: GeoOption[]
  product_templates: { uuid: string; name: string }[]
  channels: AssignmentChannel[]
  has_team: boolean
  counts: Record<string, number>
}

/** Refetch every Geography list and count, e.g. after a bulk import. */
export function useRefreshGeoData() {
  const qc = useQueryClient()
  return useCallback(() => {
    qc.invalidateQueries({
      predicate: (query) => String(query.queryKey[0] ?? '').startsWith('geo-'),
    })
  }, [qc])
}

export function useGeoMeta() {
  const enabled = useFeatureFlag(FLAGS.GEOGRAPHIC_AVAILABILITY)
  return useQuery<GeoMeta>({
    queryKey: ['geo-meta'],
    queryFn: () => api.get('/geo/meta/').then((r) => result<GeoMeta>(r.data)),
    enabled,
  })
}

// ── Assignments ───────────────────────────────────────────────────────

export interface OwnerCell {
  membership_uuid: string | null
  user_uuid: string
  name: string
  email: string
  product_template: { uuid: string; name: string } | null
  via: 'row' | 'attribute' | 'legacy'
  conflict: boolean
  others?: { membership_uuid: string; user_uuid: string; name: string; email: string }[]
}

export interface AssignmentZoneRow {
  zone_uuid: string
  zone_name: string
  zone_code: string
  zone_type: string
  owners: Record<string, OwnerCell | null>
}

export interface WildcardHolder {
  channel: string
  membership_uuid: string
  user_uuid: string
  name: string
  email: string
}

export interface AssignmentGrid extends PaginatedResponse<AssignmentZoneRow> {
  channels: AssignmentChannel[]
  wildcard_holders: WildcardHolder[]
  product_template: { uuid: string; name: string } | null
}

export function useGeoAssignments(params?: Params) {
  const enabled = useFeatureFlag(FLAGS.GEOGRAPHIC_AVAILABILITY)
  return useQuery<AssignmentGrid>({
    queryKey: ['geo-assignments', clean(params)],
    queryFn: () => api.get('/geo/assignments/', { params: clean(params) }).then((r) => r.data),
    enabled,
  })
}

export interface AssignmentMember {
  membership_uuid: string
  user_uuid: string
  name: string
  email: string
  zone_codes: string[]
  is_wildcard: boolean
}

export function useGeoAssignmentMembers(channel: string | null) {
  const enabled = useFeatureFlag(FLAGS.GEOGRAPHIC_AVAILABILITY)
  return useQuery<AssignmentMember[]>({
    queryKey: ['geo-assignment-members', channel],
    queryFn: () =>
      api
        .get('/geo/assignments/members/', { params: { channel } })
        .then((r) => result<AssignmentMember[]>(r.data)),
    enabled: enabled && !!channel,
  })
}

export interface OwnerChangeInput {
  zone_uuid: string
  channel: string
  product_template_uuid?: string | null
  membership_uuid: string | null
}

export interface OwnerChangePreview {
  count: number
  previous_owner: { user_uuid: string; name: string; email: string } | null
  new_owner: { user_uuid: string; name: string; email: string } | null
  claims: {
    uuid: string
    number: string
    state_key: string
    state_label: string
    current_owner: { user_uuid: string; name: string } | null
  }[]
}

export function usePreviewOwnerChange() {
  return useMutation<OwnerChangePreview, unknown, OwnerChangeInput>({
    mutationFn: (body) =>
      api.post('/geo/assignments/preview/', body).then((r) => result<OwnerChangePreview>(r.data)),
  })
}

export interface OwnerChangeResult {
  owner: OwnerCell | null
  row_uuid: string | null
  reassigned: number
  previewed: number
}

export function useSetOwner() {
  const qc = useQueryClient()
  return useMutation<
    OwnerChangeResult,
    unknown,
    OwnerChangeInput & { reassign_open_claims?: boolean }
  >({
    mutationFn: (body) =>
      api.post('/geo/assignments/set/', body).then((r) => result<OwnerChangeResult>(r.data)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['geo-assignments'] })
      qc.invalidateQueries({ queryKey: ['geo-assignment-members'] })
      qc.invalidateQueries({ queryKey: ['claims'] })
      qc.invalidateQueries({ queryKey: ['org-members'] })
      qc.invalidateQueries({ queryKey: ['role-attributes'] })
    },
  })
}

// ── Lookup (address -> zone -> PSA) ───────────────────────────────────

export interface GeoResolveResult {
  zone: { uuid: string; name: string; code: string; zone_type: string } | null
  psa: { uuid: string; name: string; code: string } | null
  resolution_method: string
  resolution_confidence: string
}

export function useGeoResolve() {
  return useMutation<
    GeoResolveResult,
    unknown,
    { postal_code: string; country_code?: string; state?: string; business?: string }
  >({
    mutationFn: (body) =>
      api.post('/geo/resolve/', body).then((r) => result<GeoResolveResult>(r.data)),
  })
}
